import { FunctionComponent, memo, useContext } from "react";

import Modal, { ModalContent, ModalHeader, useModal } from "@edgetier/modal";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import UserForm, { IUserFormValues } from "components-for/users/user-form";
import { axios } from "utilities/axios";
import { IUsersGetResponse, Route } from "@edgetier/watchtower-types";
import toast from "react-hot-toast";
import { getTimeToRead } from "@edgetier/utilities";
import { getServerError } from "utilities/get-server-error";
import { useQueryClient } from "react-query";
import { updateUsersCache } from "./user-edit-action.utilities";
import { EllipsisMenuContext, EllipsisMenuItem } from "@edgetier/client-components";
import { IProps } from "./user-edit-action.types";
import "./user-edit-action.scss";

/**
 * A menu action that, when clicked, will show form in a modal that can be used to edit a user.
 * @param props.user The user being edited.
 */
const UserEditAction: FunctionComponent<IProps> = ({ user }) => {
    const { lockOpen, unlock } = useContext(EllipsisMenuContext);
    const queryClient = useQueryClient();
    const { closeModal, openModal, modalProps } = useModal({ onCloseModal: unlock });

    const { alertsEnabled, firstName, surname, emailAddress, languageIsoCode, roleId, modelIds, timeZoneId } = user;

    /**
     * Make a PATCH request to the backend with the new user details.
     * @param userWithChanges The user with updated values.
     * @param errorCallback Optional callback action trigged on error.
     */
    const onSubmit = async (userWithChanges: IUserFormValues, errorCallback?: VoidFunction) => {
        try {
            await axios.patch(Route.User.replace(":userId", user.userId), userWithChanges);
            queryClient.setQueriesData<IUsersGetResponse>(
                Route.Users,
                updateUsersCache({ userId: user.userId, ...userWithChanges })
            );
            const successMessage = `Successfully updated ${userWithChanges.firstName}'s details`;
            toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
            closeModal();
        } catch (error) {
            toast.error(getServerError(error));

            if (typeof errorCallback === "function") {
                errorCallback();
            }
        }
    };

    return (
        <>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Edit User</h2>
                </ModalHeader>
                <ModalContent>
                    <UserForm
                        initialValues={{
                            alertsEnabled,
                            firstName,
                            surname,
                            emailAddress,
                            languageIsoCode,
                            roleId,
                            modelIds,
                            timeZoneId,
                        }}
                        onSubmit={onSubmit}
                    />
                </ModalContent>
            </Modal>
            <EllipsisMenuItem
                closeAfterClick={false}
                icon={faPencil}
                onClick={() => {
                    lockOpen();
                    openModal();
                }}
            >
                Edit
            </EllipsisMenuItem>
        </>
    );
};

export default memo(UserEditAction);
